import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { CreateLayoutTakeoverScheduleInput, LayoutTakeoverSchedule } from "@/lib/layout-studio/takeoverScheduleTypes";

export function useLayoutTakeoverSchedules(presetId?: string | null) {
  const [schedules, setSchedules] = useState<LayoutTakeoverSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      let query = (supabase.from as any)("deal_finder_layout_takeover_schedules").select("*").order("starts_at", { ascending: true });
      if (presetId) query = query.eq("preset_id", presetId);
      const { data, error } = await query;
      if (error) throw error;
      setSchedules((data || []) as LayoutTakeoverSchedule[]);
    } catch (err) {
      console.error("[useLayoutTakeoverSchedules] fetch failed", err);
      toast.error("Failed to load takeover schedules");
    } finally {
      setLoading(false);
    }
  }, [presetId]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const createSchedule = useCallback(async (input: CreateLayoutTakeoverScheduleInput) => {
    setSaving(true);
    try {
      const { data, error } = await (supabase.from as any)("deal_finder_layout_takeover_schedules")
        .insert([
          {
            ...input,
            timezone: input.timezone || "America/Toronto",
            schedule_type: input.schedule_type || "ONE_TIME",
            is_enabled: input.is_enabled ?? true,
          },
        ])
        .select()
        .single();
      if (error) throw error;
      setSchedules((prev) => [...prev, data as LayoutTakeoverSchedule]);
      toast.success("Takeover schedule created");
      return data as LayoutTakeoverSchedule;
    } catch (err) {
      console.error("[useLayoutTakeoverSchedules] create failed", err);
      toast.error("Failed to create takeover schedule");
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const updateSchedule = useCallback(async (id: string, updates: Partial<LayoutTakeoverSchedule>) => {
    setSaving(true);
    try {
      const { data, error } = await (supabase.from as any)("deal_finder_layout_takeover_schedules")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      setSchedules((prev) => prev.map((s) => (s.id === id ? (data as LayoutTakeoverSchedule) : s)));
      return true;
    } catch (err) {
      console.error("[useLayoutTakeoverSchedules] update failed", err);
      toast.error("Failed to update takeover schedule");
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const deleteSchedule = useCallback(async (id: string) => {
    setSaving(true);
    try {
      const { error } = await (supabase.from as any)("deal_finder_layout_takeover_schedules").delete().eq("id", id);
      if (error) throw error;
      setSchedules((prev) => prev.filter((s) => s.id !== id));
      return true;
    } catch (err) {
      console.error("[useLayoutTakeoverSchedules] delete failed", err);
      toast.error("Failed to delete takeover schedule");
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    schedules,
    loading,
    saving,
    refetch: fetchSchedules,
    createSchedule,
    updateSchedule,
    deleteSchedule,
  };
}
